<script>
import getViewConfig from './base-view-config'
import viewEvents from './mixins/view-events-mixin'

export default {
    name: 'BaseView',
    // -------------------------------- Mixins -------------------------------------
    mixins:[viewEvents],

    // -------------------------------- Injection ---------------------------------------
    inject: ['mapViewer'],

    // -------------------------------- Props ---------------------------------------
    props:{
        name: {type: String, default: null },
        selectedIds: {type: [String, Array, Number], default:() => [] },
        viewConfigOverride:{ type: Object, default: () => ({}) },
        renderTrigger:{ type: Number, default: 0 },

        allowRotation:{ type: Boolean, default: true},
        transitionDuration: {type:Number, default: 1},

        centerOnUnits: { type: Array, default: () => []},
        centerOnFloors: { type: Array, default: () => []},
        centerOnGuide: { type: Boolean, default: false},
        centerOnGuideSteps: { type: Array, default: () => []},

        zoom: { type: Number, default: 1000 },
        zoomOnUnits: { type: Array, default: () => []},
        zoomOnFloors: { type: Array, default: () => []},
        zoomOnGuide: { type: Boolean, default: false},
        zoomOnGuideSteps: { type: Array, default: () => []},
        zoomOnGuideStepRadius:{ type: Number, default: 0 },

        showFloors: { type: Array, default: () => []},
        showBuildings: { type: Array, default: () => []},
        showCovers: { type: [Array, Boolean], default: false },

        fadeOutFloors: { type: Array, default: () => []},
        fadeOutFloorsExcept: { type: Array, default: () => []},
        fadeOutCovers: { type: Array, default: () => []},

        transparentBuildings: { type: Array, default: () => []},
        transparentFloors: { type: Array, default: () => []},
        transparencyLevel: { type: Number, default: 1 },
        transparentCovers: { type: Array, default: () => []},

        cameraOrientation:{ type: Number, default: 0 },
        cameraTilt:{ type: Number, default: 31 },
        cameraZoom:{ type: Number, default: 0 },
        cameraZoomMargin:{ type: Number, default: 0.2 },

        constrainToLevel: { type: [Number, Array], default: () => [0] },

        showOutdoor: { type: Boolean, default: false },

        separateDistance: { type: [Array, Number], default: 0 },
        separateLevels: { type: [Array, Boolean], default: false},
        separateFloors: { type: [Object], default: () => {} },

        limitMaxZoomToBuildings: { type: [Array], default: () => []},
        limitMaxZoomToFloors: { type: [Array], default: () => []},
        limitMaxZoomToUnits: { type: [Array], default: () => []},
        limitMinZoomTo: { type: Number, default: 0},
        limitMaxZoomTo: { type: Number, default: 0},
        maxZoomMargin: { type: [Number,Boolean], default: false},
        minZoomMargin: { type: [Number,Boolean], default: false},
        maxZoomOffset: { type: [Number,Boolean], default: false},
        minZoomOffset: { type: [Number,Boolean], default: false},

        showIcons: { type: [Array,Boolean], default: true},

    },

    // -------------------------------- Data ---------------------------------------
    data(){
        return {
            isTransitioning: false,
            // import base view config from ./base-view-config.js
            inheritedViews: [],
            goToViewTimeout: null,
            goToViewDelay: 50,
        }
    },

    // -------------------------------- Computed ---------------------------------------
    computed:{
        baseViewConfig(){
            const view = getViewConfig()

            if(!view.camera_control_strategy)
                view.camera_control_strategy= { params: {}}

            view.camera_control_strategy.params.allow_rotation = this.allowRotation

            view.transition_duration = this.transitionDuration

            if(this.centerOnFloors.length){
                view.camera_center_strategy = {
                    type: 'center_on_floors',
                    params:{
                        selected_ids: this.centerOnFloors
                    }
                }
            }
            if(this.centerOnUnits.length){
                view.camera_center_strategy = {
                    type: 'center_on_units',
                    params:{
                        selected_ids: this.centerOnUnits
                    }
                }
            }
            if(this.centerOnGuide){
                view.camera_center_strategy = {
                    type: 'center_on_guide',
                    params:{}
                }
            }
            if(this.centerOnGuideSteps.length){
                view.camera_center_strategy = {
                    type: 'center_on_guide_steps',
                    params:{
                        selected_steps: this.centerOnGuideSteps
                    }
                }
            }

            if(this.zoom){
                view.camera_zoom_strategy = {
                    type: 'use_fixed_zoom',
                    params:{
                        value: this.zoom
                    }
                }
            }
            if(this.zoomOnFloors.length){
                view.camera_zoom_strategy = {
                    type: 'zoom_on_floors',
                    params:{
                        selected_ids: this.zoomOnFloors
                    }
                }
            }
            if(this.zoomOnUnits.length){
                view.camera_zoom_strategy = {
                    type: 'zoom_on_units',
                    params:{
                        selected_ids: this.zoomOnUnits
                    }
                }
            }
            if(this.zoomOnGuide){
                view.camera_zoom_strategy = {
                    type: 'zoom_on_guide',
                    params:{}
                }
            }

            if(this.zoomOnGuideSteps.length){
                view.camera_zoom_strategy = {
                    type: 'zoom_on_guide_steps',
                    params:{
                        selected_steps: this.zoomOnGuideSteps
                    }
                }
            }
            if(this.zoomOnGuideStepRadius){
                view.camera_zoom_strategy.params.step_radius = this.zoomOnGuideStepRadius
            }

            if(this.showFloors.length){
                view.floor_visibility_strategy_on_start = {
                    type: 'show_selection',
                    params:{
                        selected_ids: this.showFloors
                    }
                }
                view.floor_visibility_strategy_on_finish = {
                    type: 'show_selection',
                    params:{
                        selected_ids: this.showFloors
                    }
                }
            }
            if(this.showBuildings.length){
                view.floor_visibility_strategy_on_start = {
                    type: 'show_by_building',
                    params:{
                        selected_ids: this.showBuildings
                    }
                }
                view.floor_visibility_strategy_on_finish = {
                    type: 'show_by_building',
                    params:{
                        selected_ids: this.showBuildings
                    }
                }
            }

            view.floor_visibility_strategy_on_start.params.show_outdoor = this.showOutdoor;
            view.floor_visibility_strategy_on_finish.params.show_outdoor = this.showOutdoor;

            if(this.showCovers.length){
                view.cover_visibility_strategy_on_start = {
                    type: 'show_by_building',
                    params:{
                        selected_ids: this.showCovers
                    }
                }
                view.cover_visibility_strategy_on_finish = {
                    type: 'show_by_building',
                    params:{
                        selected_ids: this.showCovers
                    }
                }
            }
            if(this.showCovers === true){
                view.cover_visibility_strategy_on_start = {
                    type: 'show_all',
                    params:{
                        selected_ids: this.showCovers
                    }
                }
                view.cover_visibility_strategy_on_finish = {
                    type: 'show_all',
                    params:{
                        selected_ids: this.showCovers
                    }
                }
            }

            if(this.fadeOutFloors.length){
                view.floor_focus_color_strategy = {
                    type: 'set_on_floors',
                    params:{
                        selected_ids: this.fadeOutFloors,
                        value: 1,
                        reverse: 0,
                    }
                }
            }
            if(this.fadeOutFloorsExcept.length){
                view.floor_focus_color_strategy = {
                    type: 'set_on_floors',
                    params:{
                        selected_ids: this.fadeOutFloorsExcept,
                        value: 0,
                        reverse: 0,
                    }
                }
            }
            if(this.fadeOutCovers.length){
                view.cover_focus_color_strategy = {
                    type: 'set_on_buildings',
                    params:{
                        selected_ids: this.fadeOutCovers,
                        value: 1,
                    }
                }
            }

            if( parseInt(this.separateDistance) && this.separateLevels){
                view.floor_separation_strategy.type = 'separate_all'
                view.floor_separation_strategy.params.value = this.separateDistance
            }
            if( _.isArray(this.separateLevels) ){
                view.floor_separation_strategy.type = 'separate_by_selected_levels'
                view.floor_separation_strategy.params.selected_levels = this.separateLevels
                view.floor_separation_strategy.params.value = 0
                view.floor_separation_strategy.params.Xd = 0
                view.floor_separation_strategy.params.Xu = this.separateDistance
            }
            if( this.separateFloors ){
                view.floor_separation_strategy.type = 'separate_by_selected_floors'
                view.floor_separation_strategy.params.selected_ids = this.separateFloors
                view.floor_separation_strategy.params.value = 0
                view.floor_separation_strategy.params.Xd = 0
                view.floor_separation_strategy.params.Xu = this.separateDistance
            }

            if(this.cameraOrientation){
                view.camera_zoom_strategy.params.use_orientation = true
                view.camera_zoom_strategy.params.orientation = this.cameraOrientation
            }
            if(this.cameraTilt){
                view.camera_zoom_strategy.params.use_tilt = true
                view.camera_zoom_strategy.params.tilt = this.cameraTilt
            }
            if(this.cameraZoom){
                view.camera_zoom_strategy.params.use_zoom = true
                view.camera_zoom_strategy.params.zoom = this.cameraZoom
            }
            if(this.cameraZoomMargin){
                view.camera_zoom_strategy.params.margin = this.cameraZoomMargin
            }

            view.camera_control_strategy.params.selected_levels = Array.isArray(this.constrainToLevel) ? this.constrainToLevel : [this.constrainToLevel],

            view.event_strategy.click_on_covers = this.hasCoverClickEvent
            view.event_strategy.click_on_buildings = this.hasBuildingClickEvent
            view.event_strategy.click_on_floors = this.hasFloorClickEvent
            view.event_strategy.click_on_units = this.hasUnitClickEvent
            view.event_strategy.click_on_stores = this.hasUnitClickEvent

            if(this.transparentCovers.length){
                view.cover_transparency_strategy = {
                    type: 'set_on_buildings',
                    params:{
                        selected_ids: this.transparentCovers,
                        value: this.transparencyLevel,
                    }
                }
            }
            if(this.transparentBuildings.length){
                view.floor_transparency_strategy = {
                    type: 'set_on_buildings',
                    params:{
                        selected_ids: this.transparentBuildings,
                        value: this.transparencyLevel,
                    }
                }
            }
            if(this.transparentFloors.length){
                view.floor_transparency_strategy = {
                    type: 'set_on_floors',
                    params:{
                        selected_ids: this.transparentFloors,
                        value: this.transparencyLevel,
                    }
                }
            }

            if(this.showIcons === false){
                view.placeholder_visibility_strategy_on_start = {
                    type: 'hide_all',
                    params:{}
                };
                view.placeholder_visibility_strategy_on_finish = {
                    type: 'hide_all',
                    params:{}
                };
            }
            if(this.showIcons === true){
                view.placeholder_visibility_strategy_on_start = {
                    type: 'show_all',
                    params:{}
                };
                view.placeholder_visibility_strategy_on_finish = {
                    type: 'show_all',
                    params:{}
                }
            }
            if(this.showIcons.length){
                view.placeholder_visibility_strategy_on_start = {
                    type: 'show_selection',
                    params:{
                        selected_ids: this.showIcons,
                    }
                };
                view.placeholder_visibility_strategy_on_finish = {
                    type: 'show_selection',
                    params:{
                        selected_ids: this.showIcons,
                    }
                }
            }

            if(this.limitMinZoomTo){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.min_zoom_type = 'fixed';
                view.camera_zoom_limit_strategy.params.min_zoom_value = this.limitMinZoomTo
                view.camera_zoom_limit_strategy.params.min_zoom_margin = this.minZoomMargin || this.cameraZoomMargin
            }

            if(this.limitMaxZoomTo){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.max_zoom_type = 'fixed';
                view.camera_zoom_limit_strategy.params.max_zoom_value = this.limitMaxZoomTo
                view.camera_zoom_limit_strategy.params.max_zoom_margin = this.maxZoomMargin || this.cameraZoomMargin
            }
            if(this.minZoomOffset){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.min_zoom_type = 'offset';
                view.camera_zoom_limit_strategy.params.min_zoom_value = this.minZoomOffset
                view.camera_zoom_limit_strategy.params.min_zoom_margin = this.minZoomMargin || this.cameraZoomMargin
            }

            if(this.maxZoomOffset){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.max_zoom_type = 'offset';
                view.camera_zoom_limit_strategy.params.max_zoom_value = this.maxZoomOffset
                view.camera_zoom_limit_strategy.params.max_zoom_margin = this.maxZoomMargin || this.cameraZoomMargin
            }
            if(this.limitMaxZoomToBuildings.length){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.max_zoom_type = 'focus_on_building_zoom';
                view.camera_zoom_limit_strategy.params.max_zoom_selected_ids = this.limitMaxZoomToBuildings
                view.camera_zoom_limit_strategy.params.max_zoom_margin = this.maxZoomMargin || this.cameraZoomMargin
            }
            if(this.limitMaxZoomToFloors.length){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.max_zoom_type = 'focus_on_floor_zoom';
                view.camera_zoom_limit_strategy.params.max_zoom_selected_ids = this.limitMaxZoomToFloors
                view.camera_zoom_limit_strategy.params.max_zoom_margin = this.maxZoomMargin || this.cameraZoomMargin
            }
            if(this.limitMaxZoomToUnits.length){
                view.camera_zoom_limit_strategy.type = 'limit';
                view.camera_zoom_limit_strategy.params.max_zoom_type = 'focus_on_unit_zoom';
                view.camera_zoom_limit_strategy.params.max_zoom_selected_ids = this.limitMaxZoomToUnits
                view.camera_zoom_limit_strategy.params.max_zoom_margin = this.maxZoomMargin || this.cameraZoomMargin
            }

            return view
        },
        viewName(){ return this.name || this.$options.name },
        selectedIdsArray(){
            return Array.isArray(this.selectedIds) ? this.selectedIds : [this.selectedIds]
        },
        selectedIdsString(){
            return Array.isArray(this.selectedIds) ? this.selectedIds.join(',') : this.selectedIds
        },
        isActive(){
            return this.mapViewer.activeView === this.viewName
        },
        // All inherited views will have their own config props, we will combine them all
        inheritedViewConfigs() { return this.inheritedViews.map(viewName => this[viewName]) },
        // Merge view config with parent view configs
        mergedViewConfig(){
            const mergedView = {}
            return _.merge(
                mergedView,
                ...this.inheritedViewConfigs,
                this.viewConfigOverride,
                { name: this.viewName},
                { renderTrigger: this.renderTrigger }
            )
        },
        runtimeViewConfig(){
            return this.mergedViewConfig
        },
    },

    // -------------------------------- Methods ---------------------------------------
    methods:{
        addInheritedView(configPropName){
            this.inheritedViews.push(configPropName)
        },
        goToViewAfterTimout(){
            if( !this.isActive ) return;
            this.goToViewTimeout && clearTimeout(this.goToViewTimeout);
            this.goToViewTimeout = setTimeout(this.goToView, this.goToViewDelay);
        },
        goToView(){
            this.beforeGoToView();
            console.debug(`Mapviewer.${this.viewName}.goToView()`, this.runtimeViewConfig);
            this.$emit('transitionStart');
            this.isTransitioning = true;
            this.mapViewer.viewApi.deselect_units()
            return this.mapViewer.viewApi.go_to_view(JSON.parse(JSON.stringify(this.runtimeViewConfig))).then(() => {
                this.isTransitioning = false;
                this.onTransitionToViewComplete();
                this.$emit('transitionEnd');

            })
        },
        onTransitionToViewComplete(){
        },
        beforeGoToView(){},
        resetView(){
            this.deactivateEventsListeners();
            this.initEventsListeners();
            this.goToViewAfterTimout();
        },
    },

    // -------------------------------- Watches ---------------------------------------
    watch:{
        name(){
            this.resetView()
        },
        isActive(){
            this.resetView()
        },
        runtimeViewConfig(){
            console.debug(`Mapviewer.${this.viewName}.config.updated`)
            this.goToViewAfterTimout()
        }
    },

    // -------------------------------- Hooks ---------------------------------------
    created(){
        console.debug(`Mapviewer.${this.viewName}.created`)
        //Each child view should call this method to include their view config prop name to inheritance collection
        this.addInheritedView('baseViewConfig')
    },
    mounted(){
        console.debug(`Mapviewer.${this.viewName}.mounted`)
        // child component hooks will fire first. Prevent view to be initialised more then once, but also initialise by default, if child component didn't do this
        this.goToViewAfterTimout()
    },
    updated(){
        console.debug(`Mapviewer.${this.viewName}.updated`)
    },
    beforeDestroy(){
        console.debug(`Mapviewer.${this.viewName}.destroyed`)
    }
}
</script>
<template>
    <div v-if="isActive">
        <slot></slot>
    </div>
</template>
